// ============================================================
//  src/pages/bots/tabs/TemplatesTab.js
//  Manage WhatsApp Message Templates — create, sync, delete
// ============================================================
import React, { useEffect, useState, useCallback } from "react";
import {
  listTemplates,
  createTemplate,
  deleteTemplate,
  editTemplate,
  setTemplatePause,
  clearTemplatePause,
} from "../../../api/client";
import toast from "react-hot-toast";
import "./tabs.css";

// ── Helpers ───────────────────────────────────────────────────
const STATUS_STYLE = {
  APPROVED: { background: "#f0fdf4", color: "#16a34a" },
  PENDING: { background: "#fffbeb", color: "#d97706" },
  REJECTED: { background: "#fef2f2", color: "#dc2626" },
  PAUSED: { background: "#f9fafb", color: "#6b7280" },
};

const CATEGORY_STYLE = {
  AUTHENTICATION: { background: "rgba(99,102,241,0.1)", color: "#6366f1" },
  MARKETING: { background: "rgba(236,72,153,0.1)", color: "#ec4899" },
  UTILITY: { background: "rgba(20,184,166,0.1)", color: "#0d9488" },
};

// Pre-built component templates to guide users
const QUICK_TEMPLATES = [
  {
    label: "🔐 OTP / Copy Code",
    category: "AUTHENTICATION",
    components: [
      { type: "BODY", add_security_recommendation: true },
      { type: "FOOTER", code_expiration_minutes: 10 },
      { type: "BUTTONS", buttons: [{ type: "OTP", otp_type: "COPY_CODE" }] },
    ],
  },
  {
    label: "📣 Marketing Message",
    category: "MARKETING",
    components: [
      { type: "HEADER", format: "TEXT", text: "Special Offer 🎉" },
      { type: "BODY", text: "Hi {{1}}, get {{2}}% off your next order today!" },
      { type: "FOOTER", text: "Reply STOP to unsubscribe" },
    ],
  },
  {
    label: "📦 Order Update",
    category: "UTILITY",
    components: [
      {
        type: "BODY",
        text: "Hi {{1}}, your order #{{2}} has been {{3}}. Track it here: {{4}}",
      },
      { type: "FOOTER", text: "Thank you for shopping with us" },
    ],
  },
  {
    label: "✏️ Custom (blank)",
    category: "MARKETING",
    components: [],
  },
];

// ── WhatsApp-style live preview ────────────────────────────────
function sampleVar(n) {
  const samples = ["John", "482910", "20", "TRK-58213", "Lagos", "₦5,000"];
  return samples[(n - 1) % samples.length] || `value${n}`;
}

function fillVars(text) {
  if (!text) return text;
  return text.replace(/\{\{(\d+)\}\}/g, (_, n) => sampleVar(Number(n)));
}

function WhatsAppPreview({ components, category }) {
  const header = components.find((c) => c.type?.toUpperCase() === "HEADER");
  const body = components.find((c) => c.type?.toUpperCase() === "BODY");
  const footer = components.find((c) => c.type?.toUpperCase() === "FOOTER");
  const buttons = components.find((c) => c.type?.toUpperCase() === "BUTTONS");

  const isAuth = category === "AUTHENTICATION";
  const bodyText = isAuth
    ? `*${sampleVar(2)}* is your verification code.${
        body?.add_security_recommendation
          ? " For your security, do not share this code."
          : ""
      }`
    : fillVars(body?.text) || "Your message body will appear here…";

  const footerText = isAuth
    ? footer?.code_expiration_minutes
      ? `This code expires in ${footer.code_expiration_minutes} minutes.`
      : null
    : footer?.text
      ? fillVars(footer.text)
      : null;

  const btns = isAuth
    ? buttons?.buttons?.length
      ? [{ text: "Copy Code" }]
      : []
    : buttons?.buttons || [];

  // Bold *text* → <strong>
  function renderBoldText(text) {
    const parts = String(text).split(/(\*[^*]+\*)/g);
    return parts.map((p, i) =>
      p.startsWith("*") && p.endsWith("*") && p.length > 1 ? (
        <strong key={i}>{p.slice(1, -1)}</strong>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      ),
    );
  }

  return (
    <div
      style={{
        background: "#0b141a",
        borderRadius: 16,
        padding: 16,
        position: "sticky",
        top: 0,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#8696a0",
          marginBottom: 10,
          letterSpacing: 0.4,
          textTransform: "uppercase",
        }}
      >
        Preview
      </div>
      <div
        style={{
          background: "#005c4b",
          borderRadius: "10px 10px 10px 2px",
          padding: "8px 10px 9px",
          maxWidth: 260,
          boxShadow: "0 1px 1px rgba(0,0,0,0.2)",
          color: "#e9edef",
          fontSize: 13.5,
          lineHeight: 1.4,
        }}
      >
        {header && (
          <div style={{ marginBottom: 6 }}>
            {header.format === "TEXT" || !header.format ? (
              <div style={{ fontWeight: 800 }}>{fillVars(header.text)}</div>
            ) : (
              <div
                style={{
                  background: "rgba(255,255,255,0.08)",
                  borderRadius: 6,
                  padding: "16px 0",
                  textAlign: "center",
                  fontSize: 11,
                  color: "#8696a0",
                  marginBottom: 2,
                }}
              >
                [{header.format} header]
              </div>
            )}
          </div>
        )}

        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {renderBoldText(bodyText)}
        </div>

        {footerText && (
          <div style={{ fontSize: 11.5, color: "#8696a0", marginTop: 6 }}>
            {footerText}
          </div>
        )}

        <div
          style={{
            fontSize: 10.5,
            color: "#8696a0",
            textAlign: "right",
            marginTop: 4,
          }}
        >
          10:24 AM
        </div>
      </div>

      {btns.length > 0 && (
        <div
          style={{
            maxWidth: 260,
            marginTop: 1,
            background: "#005c4b",
            borderRadius: "0 0 10px 10px",
            overflow: "hidden",
          }}
        >
          {btns.map((b, i) => (
            <div
              key={i}
              style={{
                textAlign: "center",
                padding: "9px 0",
                fontSize: 13,
                fontWeight: 600,
                color: "#53bdeb",
                borderTop: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              {b.type === "PHONE_NUMBER" && "📞 "}
              {b.type === "URL" && "🔗 "}
              {b.text || b.otp_type || "Button"}
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          fontSize: 11,
          color: "#8696a0",
          marginTop: 12,
          lineHeight: 1.5,
        }}
      >
        💡 Sample values shown for <code>{"{{n}}"}</code> variables. Actual
        messages will use real customer data.
      </div>
    </div>
  );
}


function TemplateModal({ bId, mode = "create", initial, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [step, setStep] = useState(isEdit ? 2 : 1); // 1=pick starter, 2=fill form
  const [starter, setStarter] = useState(null);
  const [name, setName] = useState(initial?.name ?? "");
  const [category, setCategory] = useState(initial?.category ?? "AUTHENTICATION");
  const [language, setLanguage] = useState(initial?.language ?? "en_US");
  const [compJson, setCompJson] = useState(
    JSON.stringify(initial?.components ?? [], null, 2),
  );
  const [jsonErr, setJsonErr] = useState("");
  const [saving, setSaving] = useState(false);

  function pickStarter(t) {
    setStarter(t);
    setCategory(t.category);
    setCompJson(JSON.stringify(t.components, null, 2));
    setStep(2);
  }

  function validateJson(val) {
    setCompJson(val);
    try {
      JSON.parse(val);
      setJsonErr("");
    } catch (e) {
      setJsonErr("Invalid JSON: " + e.message);
    }
  }

  async function handleSave() {
    if (!name.trim()) return toast.error("Template name is required.");
    if (jsonErr) return toast.error("Fix JSON errors first.");
    let components;
    try {
      components = JSON.parse(compJson);
    } catch {
      return toast.error("Invalid JSON in components.");
    }

    setSaving(true);
    try {
      if (isEdit) {
        const res = await editTemplate(bId, name.trim(), { components });
        toast.success(
          `Template "${name}" updated! It will be re-reviewed by Meta. Status: ${res.data?.status ?? "PENDING"}`,
        );
      } else {
        const res = await createTemplate(bId, {
          name: name.trim(),
          category,
          language,
          components,
        });
        toast.success(
          `Template "${name}" created! Status: ${res.data?.status ?? "PENDING"}`,
        );
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.message ||
          `Failed to ${isEdit ? "update" : "create"} template.`,
      );
    } finally {
      setSaving(false);
    }
  }

  let parsedComponents = [];
  try {
    parsedComponents = JSON.parse(compJson);
    if (!Array.isArray(parsedComponents)) parsedComponents = [];
  } catch {
    parsedComponents = [];
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal modal-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: step === 2 ? 920 : 640 }}
      >
        <div className="modal-header">
          <div className="modal-title">
            {isEdit ? "✏️ Edit Template" : "📝 Create Template"}
          </div>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* Step 1 — pick a starter */}
          {step === 1 && (
            <>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text-muted)",
                  marginBottom: 16,
                }}
              >
                Pick a starter to get going quickly, or start from scratch.
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                {QUICK_TEMPLATES.map((t, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => pickStarter(t)}
                    style={{
                      textAlign: "left",
                      padding: "14px 16px",
                      border: "1.5px solid var(--border)",
                      borderRadius: 10,
                      background: "var(--surface)",
                      cursor: "pointer",
                      transition: "border-color .15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.borderColor = "var(--primary)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.borderColor = "var(--border)")
                    }
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13.5,
                        marginBottom: 4,
                      }}
                    >
                      {t.label}
                    </div>
                    <span
                      style={{
                        ...(CATEGORY_STYLE[t.category] || {}),
                        fontSize: 11,
                        fontWeight: 700,
                        borderRadius: 6,
                        padding: "2px 7px",
                      }}
                    >
                      {t.category}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step 2 — fill form */}
          {step === 2 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 280px",
                gap: 24,
                alignItems: "start",
              }}
            >
              <div>
              {!isEdit && (
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--primary)",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 700,
                    padding: 0,
                    marginBottom: 16,
                  }}
                >
                  ← Back
                </button>
              )}

              <div className="form-group">
                <label className="form-label">
                  Template Name{" "}
                  <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  className="form-input"
                  placeholder="e.g. otp_verification (lowercase, underscores only)"
                  value={name}
                  disabled={isEdit}
                  onChange={(e) =>
                    setName(
                      e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                    )
                  }
                />
                <div
                  style={{
                    fontSize: 11.5,
                    color: "var(--text-muted)",
                    marginTop: 4,
                  }}
                >
                  {isEdit
                    ? "Template name can't be changed. Delete and recreate if you need a different name."
                    : "Only lowercase letters, numbers, and underscores allowed."}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    className="form-input"
                    value={category}
                    disabled={isEdit}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="AUTHENTICATION">AUTHENTICATION</option>
                    <option value="MARKETING">MARKETING</option>
                    <option value="UTILITY">UTILITY</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Language</label>
                  <select
                    className="form-input"
                    value={language}
                    disabled={isEdit}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    <option value="en_US">English (en_US)</option>
                    <option value="en">English (en)</option>
                    <option value="fr">French (fr)</option>
                    <option value="es">Spanish (es)</option>
                    <option value="pt_BR">Portuguese BR (pt_BR)</option>
                    <option value="ar">Arabic (ar)</option>
                    <option value="ha">Hausa (ha)</option>
                    <option value="yo">Yoruba (yo)</option>
                    <option value="ig">Igbo (ig)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <label className="form-label" style={{ margin: 0 }}>
                    Components (JSON)
                  </label>
                  <a
                    href="https://developers.facebook.com/docs/whatsapp/cloud-api/reference/messages"
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 11.5, color: "var(--primary)" }}
                  >
                    Meta docs ↗
                  </a>
                </div>
                <textarea
                  className="form-input"
                  style={{
                    fontFamily: "monospace",
                    fontSize: 12.5,
                    minHeight: 220,
                    resize: "vertical",
                  }}
                  value={compJson}
                  onChange={(e) => validateJson(e.target.value)}
                />
                {jsonErr && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--danger)",
                      marginTop: 4,
                    }}
                  >
                    {jsonErr}
                  </div>
                )}
                {isEdit && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text-muted)",
                      marginTop: 6,
                      lineHeight: 1.6,
                    }}
                  >
                    💡 Editing resets this template's status to{" "}
                    <strong>PENDING</strong> for re-review by Meta. Approved
                    templates have limits on how much the wording can change.
                  </div>
                )}
                {category === "AUTHENTICATION" && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text-muted)",
                      marginTop: 8,
                      lineHeight: 1.6,
                      background: "rgba(99,102,241,0.06)",
                      border: "1px solid rgba(99,102,241,0.2)",
                      borderRadius: 8,
                      padding: "10px 12px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        color: "var(--text-dark)",
                        marginBottom: 4,
                      }}
                    >
                      ⚠️ Authentication templates can't have custom text
                    </div>
                    Meta locks the wording — you can only toggle these:
                    <ul style={{ margin: "6px 0 6px 18px", padding: 0 }}>
                      <li>
                        <code>BODY.add_security_recommendation</code> →
                        appends "For your security, do not share this code."
                      </li>
                      <li>
                        <code>FOOTER.code_expiration_minutes</code> → appends
                        "This code expires in N minutes." (drop the FOOTER
                        block entirely to omit it)
                      </li>
                      <li>
                        <code>BUTTONS</code> — exactly one button, type must
                        be <code>"OTP"</code> with{" "}
                        <code>otp_type</code> = <code>COPY_CODE</code>,{" "}
                        <code>ONE_TAP</code>, or <code>ZERO_TAP</code>. No
                        URL/quick-reply buttons.
                      </li>
                    </ul>
                    No header, no images, no emojis, no extra lines (e.g. a
                    support contact number) are allowed in this category —
                    Meta will reject them. Want a "contact us if this
                    wasn't you" note? Send it as a separate{" "}
                    <strong>Utility</strong> or <strong>Marketing</strong>{" "}
                    template right after the OTP instead.
                  </div>
                )}
              </div>
              </div>

              <WhatsAppPreview
                components={parsedComponents}
                category={category}
              />
            </div>
          )}
        </div>

        {step === 2 && (
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !!jsonErr}
            >
              {saving ? (
                <>
                  <span className="spinner" /> {isEdit ? "Saving…" : "Creating…"}
                </>
              ) : isEdit ? (
                "💾 Save Changes"
              ) : (
                "📝 Create Template"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Template Card ─────────────────────────────────────────────
// ── Configure a local pause rule for one template ───────────────
// NOTE: this is NOT a Meta feature. Meta has no API for time-boxing
// templates. This rule only blocks sends going out through THIS
// platform's own broadcasts/automations/api-send — it's enforced in
// WhatsAppSender::sendTemplate(), not on Meta's side.
function PauseModal({ template, onSave, onClose }) {
  const [mode, setMode] = useState("always"); // 'always' | 'schedule'
  const [dailyStart, setDailyStart] = useState("22:00");
  const [dailyEnd, setDailyEnd] = useState("06:00");
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(template.name, {
        alwaysPaused: mode === "always",
        dailyStart: mode === "schedule" ? dailyStart : null,
        dailyEnd: mode === "schedule" ? dailyEnd : null,
        timezone,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div className="modal-title">⏸ Pause "{template.name}"</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
            Stops this template from being sent through your broadcasts, automations,
            and Send API — Meta itself has no concept of pausing a template, so this
            only blocks sends made through this platform.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="radio" checked={mode === "always"} onChange={() => setMode("always")} />
              Pause completely (until resumed)
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="radio" checked={mode === "schedule"} onChange={() => setMode("schedule")} />
              Pause only during a daily time window
            </label>
          </div>

          {mode === "schedule" && (
            <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 600 }}>From</label>
                <input type="time" className="form-input" value={dailyStart}
                  onChange={(e) => setDailyStart(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 600 }}>To</label>
                <input type="time" className="form-input" value={dailyEnd}
                  onChange={(e) => setDailyEnd(e.target.value)} />
              </div>
            </div>
          )}
          {mode === "schedule" && (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Timezone: <b>{timezone}</b> · Overnight windows (e.g. 22:00 → 06:00) are supported.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Pause Rule"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplateCard({ template, onDelete, onEdit, onPauseClick, onUnpause }) {
  const [deleting, setDeleting] = useState(false);
  const [unpausing, setUnpausing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const statusStyle = STATUS_STYLE[template.status] || STATUS_STYLE.PAUSED;
  const categoryStyle = CATEGORY_STYLE[template.category] || {};
  const pause = template.pause; // { alwaysPaused, dailyStart, dailyEnd, timezone } | null

  async function handleUnpause() {
    setUnpausing(true);
    try {
      await onUnpause(template.name);
    } finally {
      setUnpausing(false);
    }
  }

  async function handleDelete() {
    if (
      !window.confirm(
        `Delete template "${template.name}"? This cannot be undone.`,
      )
    )
      return;
    setDeleting(true);
    try {
      await onDelete(template.name);
    } finally {
      setDeleting(false);
    }
  }

  // Render a human-readable summary of components
  function renderComponents() {
    if (!template.components?.length) return null;
    return template.components.map((c, i) => {
      const type = c.type?.toUpperCase();
      let preview = "";
      if (type === "BODY")
        preview = c.text
          ? `"${c.text.slice(0, 80)}${c.text.length > 80 ? "…" : ""}"`
          : "(preset authentication body)";
      if (type === "HEADER") preview = c.text ? `"${c.text}"` : `[${c.format}]`;
      if (type === "FOOTER")
        preview = c.text
          ? `"${c.text}"`
          : c.code_expiration_minutes
            ? `Expires in ${c.code_expiration_minutes} min`
            : "";
      if (type === "BUTTONS")
        preview =
          c.buttons?.map((b) => b.text || b.otp_type || b.type).join(" · ") ||
          "";
      return (
        <div
          key={i}
          style={{ display: "flex", gap: 8, fontSize: 12.5, marginBottom: 5 }}
        >
          <span
            style={{ fontWeight: 700, color: "var(--primary)", minWidth: 70 }}
          >
            {type}
          </span>
          <span style={{ color: "var(--text-muted)", wordBreak: "break-word" }}>
            {preview}
          </span>
        </div>
      );
    });
  }

  return (
    <div
      style={{
        border: "1.5px solid var(--border)",
        borderRadius: 12,
        padding: "16px 18px",
        background: "var(--surface)",
        marginBottom: 12,
        transition: "border-color .15s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        {/* Left */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <span
              style={{
                fontWeight: 800,
                fontSize: 14,
                color: "var(--text-dark)",
                wordBreak: "break-all",
              }}
            >
              {template.name}
            </span>
            <span
              style={{
                ...statusStyle,
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 6,
                padding: "2px 7px",
              }}
            >
              {template.status}
            </span>
            <span
              style={{
                ...categoryStyle,
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 6,
                padding: "2px 7px",
              }}
            >
              {template.category}
            </span>
            <span
              style={{
                background: "var(--bg)",
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 6,
                padding: "2px 7px",
                color: "var(--text-muted)",
              }}
            >
              {template.language}
            </span>
            {pause && (
              <span
                title={
                  pause.alwaysPaused
                    ? "This template is fully paused — won't send via broadcasts/automations until resumed."
                    : `Paused daily ${pause.dailyStart}–${pause.dailyEnd} (${pause.timezone})`
                }
                style={{
                  background: "#fffbeb",
                  color: "#b45309",
                  fontSize: 11,
                  fontWeight: 700,
                  borderRadius: 6,
                  padding: "2px 7px",
                }}
              >
                ⏸ {pause.alwaysPaused ? "Paused" : `Paused ${pause.dailyStart}–${pause.dailyEnd}`}
              </span>
            )}
          </div>

          {/* Components preview */}
          {expanded ? (
            <div
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: "1px solid var(--border)",
                display: "grid",
                gridTemplateColumns: "1fr 240px",
                gap: 16,
                alignItems: "start",
              }}
            >
              <div>{renderComponents()}</div>
              <WhatsAppPreview
                components={template.components || []}
                category={template.category}
              />
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {template.components?.length ?? 0} component(s)
              {template.components?.find((c) => c.type === "BODY")?.text && (
                <span>
                  {" "}
                  · "
                  {template.components
                    .find((c) => c.type === "BODY")
                    .text.slice(0, 60)}
                  …"
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button
            className="btn btn-outline"
            style={{ padding: "5px 10px", fontSize: 12 }}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Hide" : "Details"}
          </button>
          <button
            className="btn btn-outline"
            style={{ padding: "5px 10px", fontSize: 12 }}
            onClick={() => onEdit(template)}
          >
            ✏️ Edit
          </button>
          {pause ? (
            <button
              className="btn btn-outline"
              style={{ padding: "5px 10px", fontSize: 12, color: "#16a34a", borderColor: "#16a34a" }}
              onClick={handleUnpause}
              disabled={unpausing}
            >
              {unpausing ? "…" : "▶ Resume"}
            </button>
          ) : (
            <button
              className="btn btn-outline"
              style={{ padding: "5px 10px", fontSize: 12, color: "#b45309", borderColor: "#b45309" }}
              onClick={() => onPauseClick(template)}
            >
              ⏸ Pause
            </button>
          )}
          <button
            className="icon-btn danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? (
              <span
                className="spinner spinner-dark"
                style={{ width: 12, height: 12 }}
              />
            ) : (
              "🗑"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Tab ──────────────────────────────────────────────────
export default function TemplatesTab({ bot }) {
  const bId = bot?.bId;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // template being edited, or null
  const [pausingTemplate, setPausingTemplate] = useState(null); // template being configured for pause
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL"); // ALL | APPROVED | PENDING | REJECTED

  const load = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      else setSyncing(true);
      try {
        const res = await listTemplates(bId);
        setTemplates(res.data?.templates ?? []);
      } catch (e) {
        toast.error(e.response?.data?.message || "Failed to load templates.");
      } finally {
        setLoading(false);
        setSyncing(false);
      }
    },
    [bId],
  );

  useEffect(() => {
    load();
  }, [load]);

  async function handleDelete(name) {
    try {
      await deleteTemplate(bId, name);
      toast.success(`Template "${name}" deleted.`);
      setTemplates((prev) => prev.filter((t) => t.name !== name));
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to delete template.");
    }
  }

  async function handlePauseSave(name, rule) {
    try {
      await setTemplatePause(bId, name, rule);
      toast.success(`"${name}" paused.`);
      setTemplates((prev) =>
        prev.map((t) => (t.name === name ? { ...t, pause: rule } : t)),
      );
      setPausingTemplate(null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save pause rule.");
    }
  }

  async function handleUnpause(name) {
    try {
      await clearTemplatePause(bId, name);
      toast.success(`"${name}" resumed.`);
      setTemplates((prev) =>
        prev.map((t) => (t.name === name ? { ...t, pause: null } : t)),
      );
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to resume template.");
    }
  }

  // Check if WABA ID is set
  if (!bot?.bWhatsappId) {
    return (
      <div className="tab-content">
        <div className="tab-empty">
          <div className="tab-empty-icon">📝</div>
          <div className="tab-empty-title">
            WhatsApp Business Account ID Required
          </div>
          <div className="tab-empty-text">
            To manage templates, your bot needs a{" "}
            <strong>WhatsApp Business Account ID (WABA ID)</strong>. Go to{" "}
            <strong>Overview → Edit Bot</strong> and add your WABA ID (found in
            Meta Business Manager). It is different from the Phone Number ID.
          </div>
        </div>
      </div>
    );
  }

  if (loading)
    return (
      <div className="tab-loading">
        <span className="spinner spinner-dark" /> Loading templates…
      </div>
    );

  // Filter
  const filtered = templates.filter((t) => {
    const matchesSearch =
      !search || t.name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === "ALL" || t.status === filter;
    return matchesSearch && matchesFilter;
  });

  const counts = {
    ALL: templates.length,
    APPROVED: templates.filter((t) => t.status === "APPROVED").length,
    PENDING: templates.filter((t) => t.status === "PENDING").length,
    REJECTED: templates.filter((t) => t.status === "REJECTED").length,
  };

  return (
    <div className="tab-content">
      {/* Toolbar */}
      <div className="tab-toolbar">
        <div>
          <div className="tab-title">📝 Message Templates</div>
          <div className="tab-sub">
            {templates.length} template(s) synced from Meta
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn btn-outline"
            onClick={() => load(false)}
            disabled={syncing}
          >
            {syncing ? (
              <>
                <span className="spinner spinner-dark" /> Syncing…
              </>
            ) : (
              "🔄 Sync"
            )}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreate(true)}
          >
            <span className="btn-plus">+</span> New Template
          </button>
        </div>
      </div>

      {/* Search + filter */}
      {templates.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <input
            className="form-input"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 180, maxWidth: 300 }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            {["ALL", "APPROVED", "PENDING", "REJECTED"].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                style={{
                  padding: "5px 10px",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  border: "1.5px solid",
                  borderColor:
                    filter === f ? "var(--primary)" : "var(--border)",
                  background:
                    filter === f ? "var(--primary-light)" : "var(--surface)",
                  color: filter === f ? "var(--primary)" : "var(--text-muted)",
                  cursor: "pointer",
                }}
              >
                {f} <span style={{ opacity: 0.7 }}>({counts[f]})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty */}
      {templates.length === 0 && (
        <div className="tab-empty">
          <div className="tab-empty-icon">📝</div>
          <div className="tab-empty-title">No templates yet</div>
          <div className="tab-empty-text">
            Create your first template to start sending OTPs, marketing
            messages, or notifications via WhatsApp.
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreate(true)}
          >
            + Create Template
          </button>
        </div>
      )}

      {/* List */}
      {filtered.map((t) => (
        <TemplateCard
          key={t.id || t.name}
          template={t}
          onDelete={handleDelete}
          onEdit={setEditing}
          onPauseClick={setPausingTemplate}
          onUnpause={handleUnpause}
        />
      ))}

      {filtered.length === 0 && templates.length > 0 && (
        <div className="tab-empty">
          <div className="tab-empty-text">No templates match your search.</div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <TemplateModal
          bId={bId}
          mode="create"
          onClose={() => setShowCreate(false)}
          onSaved={() => load(false)}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <TemplateModal
          bId={bId}
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => load(false)}
        />
      )}

      {/* Pause modal */}
      {pausingTemplate && (
        <PauseModal
          template={pausingTemplate}
          onSave={handlePauseSave}
          onClose={() => setPausingTemplate(null)}
        />
      )}
    </div>
  );
}